'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

function ClientPortalLoginContent() {
  const searchParams = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown> | null>(null);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'no_account') {
      setError(
        'No portal account was found for your credentials. Please contact your Pretium Agency representative.'
      );
    } else if (errorParam === 'session_expired') {
      setError('Your session has expired. Please sign in again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const supabase = createClient();
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Ignore sign-out errors
      }

      const res = await fetch('/api/client-portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      let json: Record<string, unknown> = {};
      try {
        const text = await res.text();
        json = text ? JSON.parse(text) : {};
      } catch {
        setError('An unexpected server error occurred. Please try again.');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        setError(json.error || 'Login failed. Please try again.');
        if (json.debug) {
          setDebugInfo(json.debug);
          console.error('[portal-login] Debug info:', json.debug);
        }
        setLoading(false);
        return;
      }

      // Clear any stale session/refresh tokens before hydrating the new session.
      // A leftover refresh token causes "refresh_token_not_found" on the next getSession() call.
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Ignore — we just want storage cleared
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: json.session.access_token,
        refresh_token: json.session.refresh_token,
      });

      if (sessionError) {
        console.warn('[portal-login] setSession warning:', sessionError.message);
      }

      await new Promise((resolve) => setTimeout(resolve, 150));
      window.location.href = '/client-portal';
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (err: unknown) {
      setForgotError(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-navy flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue/3 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8 gap-3">
          <AppLogo size={44} text="Pretium+" />
          <h1 className="font-display text-3xl font-bold text-white mt-2 text-center">
            Client Portal
          </h1>
          <p className="text-silver text-sm text-center mt-1">
            Sign in to access your policy documents and information.
          </p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          {!showForgotPassword ? (
            <>
              <h2 className="font-display text-2xl font-bold text-white mb-1">Sign in</h2>
              <p className="text-silver text-sm mb-6">
                Enter your credentials to access your portal.
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Email field */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="portal-email" className="text-xs font-semibold uppercase tracking-wider text-silver">
                    Email address
                  </label>
                  <input
                    id="portal-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all duration-200"
                    required
                  />
                </div>

                {/* Password field */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="portal-password" className="text-xs font-semibold uppercase tracking-wider text-silver">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotEmail(email);
                        setForgotSent(false);
                        setForgotError('');
                      }}
                      className="text-xs text-blue hover:underline transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="portal-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 pr-11 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all duration-200"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-silver hover:text-white transition-colors duration-200"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-red-alert/10 border border-red-alert/30">
                    <svg className="w-4 h-4 text-red-alert flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <div className="flex flex-col gap-1 w-full">
                      <p className="text-red-alert text-xs leading-relaxed">{error}</p>
                      {debugInfo && (
                        <details className="mt-2">
                          <summary className="text-xs text-silver cursor-pointer hover:text-white">Show diagnostic info</summary>
                          <pre className="mt-1 text-xs text-silver/70 overflow-auto max-h-40 bg-navy-mid/50 rounded p-2 whitespace-pre-wrap break-all">
                            {JSON.stringify(debugInfo, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue hover:bg-blue/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mt-1"
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in…
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="flex items-center gap-1.5 text-silver hover:text-white text-sm mb-5 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back to sign in
              </button>

              <h2 className="font-display text-2xl font-bold text-white mb-1">Reset password</h2>
              <p className="text-silver text-sm mb-6">
                Enter your email and we&apos;ll send you a reset link.
              </p>

              {forgotSent ? (
                <div className="flex items-start gap-3 px-4 py-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-green-300 text-sm leading-relaxed">
                    Password reset email sent. Please check your inbox and follow the link to reset your password.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="forgot-email" className="text-xs font-semibold uppercase tracking-wider text-silver">
                      Email address
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all duration-200"
                      required
                    />
                  </div>

                  {forgotError && (
                    <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-red-alert/10 border border-red-alert/30">
                      <svg className="w-4 h-4 text-red-alert flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      <p className="text-red-alert text-xs leading-relaxed">{forgotError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-blue hover:bg-blue/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    {forgotLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <p className="text-center text-silver/50 text-xs mt-6">
          Pretium Agency · Secure Client Portal
        </p>
      </div>
    </main>
  );
}

export default function ClientPortalLoginPage() {
  return (
    <Suspense fallback={
      <main className="min-h-screen bg-navy flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-8 h-8 animate-spin text-blue" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-silver text-sm">Loading…</p>
        </div>
      </main>
    }>
      <ClientPortalLoginContent />
    </Suspense>
  );
}
